import type { Lot, LotEvent } from '../../types/lot'
import { farmDayKey } from '../../utils/farmTime'

// Findings #199 (back-fill): a record imported for a past day must belong to
// the unit the lot was in that day. A lot's units over time come from its
// stocking date and its whole-lot moves (Trasladar lote to a unit without that
// lot). A "join" (its fish passing into the same lot in another unit) keeps
// the lot where it is but closes it: no record of it in that unit after that.

export type LotPlacement = {
  unitId: string
  /** First farm day (YYYY-MM-DD) in this unit; null = since the lot exists. */
  fromDay: string | null
  /** Exact arrival time, when it came by a move. */
  fromMs: number | null
  /** Last farm day in this unit; null = still there. */
  toDay: string | null
  /** Exact leaving time (a move out, or the move that passed all its fish out). */
  toMs: number | null
}

type LotLike = Pick<Lot, 'id' | 'productionUnitId' | 'stockingDate' | 'createdAt' | 'stage'>

const record = (value: unknown): Record<string, unknown> | undefined => (
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : undefined
)

const touchesLot = (event: LotEvent, lotId: string): boolean => (
  event.sourceLotIds.includes(lotId) || event.targetLotIds.includes(lotId)
)

/** The lot's units over time, oldest first. Empty for a lot that sits in no unit. */
export const buildLotPlacements = (lot: LotLike, events: readonly LotEvent[], timeZone: string): LotPlacement[] => {
  const ordered = events
    .filter((event) => touchesLot(event, lot.id))
    .slice()
    .sort((a, b) => Date.parse(a.recordedAt) - Date.parse(b.recordedAt))

  const moves = ordered.filter((event) => (
    (event.eventType === 'move' || event.eventType === 'transfer')
    && !record(record(event.metadata)?.fishMove)
    && event.toLocationType === 'production-unit'
    && Boolean(event.toLocationId)
  ))

  const startDay = farmDayKey(lot.stockingDate ?? '', timeZone) ?? null
  const placements: LotPlacement[] = []
  let unitId = moves.length > 0
    ? (moves[0].fromLocationType === 'production-unit' ? String(moves[0].fromLocationId ?? '') : '')
    : String(lot.productionUnitId ?? '')
  let fromDay = startDay
  let fromMs: number | null = null
  for (const move of moves) {
    const at = Date.parse(move.recordedAt)
    if (unitId) {
      placements.push({ unitId, fromDay, fromMs, toDay: farmDayKey(move.recordedAt, timeZone), toMs: at })
    }
    unitId = String(move.toLocationId ?? '')
    fromDay = farmDayKey(move.recordedAt, timeZone)
    fromMs = at
  }
  if (unitId) {
    // An Inactivo lot whose fish all passed into the same lot elsewhere ends there.
    const lastJoinOut = lot.stage === 'Inactive'
      ? ordered.filter((event) => {
          const fishMove = record(record(event.metadata)?.fishMove)
          return fishMove?.direction === 'out' && fishMove.sourceLotId === lot.id
        }).pop()
      : undefined
    placements.push({
      unitId,
      fromDay,
      fromMs,
      toDay: lastJoinOut ? farmDayKey(lastJoinOut.recordedAt, timeZone) : null,
      toMs: lastJoinOut ? Date.parse(lastJoinOut.recordedAt) : null
    })
  }
  return placements
}

/** The placement in `unitId` that covers farm day `day`, if any. */
export const findPlacementOnDay = (placements: readonly LotPlacement[], unitId: string, day: string): LotPlacement | undefined => (
  placements.find((placement) => (
    placement.unitId === unitId
    && (placement.fromDay === null || placement.fromDay <= day)
    && (placement.toDay === null || day <= placement.toDay)
  ))
)
